import React from 'react';
import { cn } from '@/utils';
import styles from './Logo.module.scss';

export type LogoType = 'lettertype' | 'symbol';
export type LogoMode = 'light' | 'dark';

export interface LogoProps {
  type?: LogoType;
  mode?: LogoMode;
  className?: string;
  ariaLabel?: string;
}

const MARKS: Record<LogoType, { viewBox: string; art: React.ReactNode }> = {
  symbol: {
    viewBox: '0 0 35.19 37.37',
    art: (
      <polygon points="10.23 30.04 15.03 30.04 35.19 0 25.48 0 10.12 25.07 10.12 15.7 0 15.7 0 37.37 10.22 37.37 10.23 30.04" />
    ),
  },
  lettertype: {
    viewBox: '0 0 287.22 52.5',
    art: (
      <>
        <polygon points="189.03 1.94 149.07 1.94 149.07 9.87 163.82 9.87 163.82 51.72 174.28 51.72 174.28 9.87 189.03 9.87 189.03 1.94" />
        <polygon points="204.05 15.55 193.86 15.55 193.86 46.56 189.03 46.56 189.03 51.58 208.82 51.58 208.82 46.56 204.05 46.56 204.05 15.55" />
        <path d="M245.71,28.29l-.07-.61c-.18-3.57-1.64-6.66-4.38-9.27-2.74-2.61-6.71-3.92-11.94-3.92-5.5,0-9.91,1.66-13.25,4.97-3.34,3.32-5.01,7.81-5.01,13.49v1.09c0,5.68,1.67,10.17,5.01,13.49,3.34,3.32,7.76,4.97,13.25,4.97s9.25-1.33,12.09-4c2.84-2.67,4.26-5.78,4.26-9.35v-.41h-9.88v.37c0,1.55-.49,2.91-1.46,4.1-.98,1.2-2.68,1.79-5.11,1.79-2.66,0-4.62-.94-5.89-2.83s-1.91-4.41-1.91-7.56v-2.25c0-3.16.66-5.67,1.98-7.55,1.32-1.87,3.27-2.81,5.86-2.81,2.2,0,3.85.56,4.94,1.67s1.64,2.5,1.64,4.15v.44h9.88Z" />
        <polygon points="267.01 38.41 274.64 51.48 286.6 51.48 272.44 30.26 267.01 38.41" />
        <polygon points="277.51 0 262.14 25.07 262.14 15.7 252.03 15.7 252.03 37.37 262.14 37.37 287.22 0 277.51 0" />
        <rect x="252.03" y="41.64" width="10.12" height="9.92" />
        <path d="M40.08,15.93l5.41-13.88H8.72l-.77,4.26h.7c1.4,0,2.51.16,3.35.49.83.33,1.16,1,.98,2.02l-6.28,35.93c-.19,1.12-.77,1.83-1.74,2.13-.98.3-2.12.45-3.42.45h-.77l-.77,4.26h39.28l6.2-15.63h-5.44s-3.34,9.02-3.98,10.16c-1.42.23-18.55.44-18.55.44l3.04-17.04h13.29l2.5-6.26h-14.67l2.89-16.18s12.18,0,13.67-.03c-.65,2.27-3.08,8.88-3.08,8.88h4.93Z" />
        <path d="M76.25,16c-1.02,2.61-2.06,5.25-3.11,7.92-1.05,2.68-2.08,5.35-3.1,8.02-1.02,2.68-2.06,5.35-3.11,8.02-.67,1.7-1.32,3.39-1.98,5.08l-3.71-29.05h-13.33l-1.54,3.95,1.54.3c.88.14,1.61.32,2.16.53s.99.47,1.29.77.5.76.59,1.36l4.11,28.67h10.54c1.44-2.88,2.89-5.78,4.36-8.68,1.47-2.91,2.92-5.81,4.36-8.72,1.44-2.91,2.9-5.81,4.36-8.72,1.46-2.91,4.69-9.45,4.69-9.45h-8.14Z" />
        <path d="M139.21,41.53c.11-.79.27-1.67.45-2.65L146.5.3h-15.21l-.77,4.19.7.14c.93.14,1.67.3,2.23.49s.97.44,1.22.77.32.77.17,1.32l-3.65,20.65c-3.52,5.21-10.58,16.01-11.02,16.65-.46.68-1.34,2.34-2.46,1.76-.39-.2-.52-.72-.52-1.32,0-.37.03-.85.11-1.43.07-.58.17-1.25.31-2.02.14-.77.28-1.66.42-2.69l4.19-23.23h-7.11l-2.18,3.33c-.64-.72-1.34-1.36-2.16-1.9-1.72-1.14-3.91-1.71-6.56-1.71-2.47,0-4.74.39-6.84,1.19-2.09.79-3.98,1.93-5.65,3.42-1.67,1.49-3.11,3.21-4.29,5.16-1.19,1.95-2.1,4.07-2.75,6.35-.65,2.28-.98,4.65-.98,7.12,0,2.98.5,5.5,1.5,7.57,1,2.07,2.4,3.65,4.19,4.74,1.79,1.09,3.92,1.64,6.38,1.64s4.42-.58,6.14-1.74,3.31-2.82,4.78-4.99c.17-.25.35-.57.53-.84,0,.01,0,.03,0,.04,0,1.58.32,2.91.98,3.98.65,1.07,1.56,1.88,2.72,2.44,1.16.55,2.53.83,4.11.83,1.07,0,2.07-.1,3-.31.93-.21,1.78-.48,2.54-.8.46-.19,5.08-7.36,8.85-13.21-.18,1.05-.34,1.99-.47,2.77-.16.96-.29,1.78-.38,2.48-.1.7-.14,1.37-.14,2.03,0,1.53.35,2.82,1.05,3.87.7,1.04,1.66,1.84,2.9,2.37,1.23.54,2.64.8,4.22.8,1.12,0,2.15-.1,3.11-.31.95-.21,1.81-.47,2.58-.77s1.38-.61,1.85-.94l.84-4.46c-.19.05-.5.15-.94.31-.44.16-.93.32-1.46.45-.54.14-1.08.21-1.64.21-.7,0-1.22-.14-1.57-.42-.35-.28-.52-.72-.52-1.32,0-.42.04-.9.11-1.43.07-.53.16-1.2.28-1.99ZM105.79,41.95c-1.09,1.63-2.17,2.86-3.24,3.7s-2.19,1.26-3.35,1.26c-.98,0-1.81-.29-2.51-.87-.7-.58-1.21-1.45-1.53-2.62-.33-1.16-.49-2.65-.49-4.46s.16-3.46.49-5.23c.33-1.77.79-3.46,1.4-5.09s1.34-3.08,2.2-4.36c.86-1.28,1.84-2.3,2.93-3.07,1.09-.77,2.27-1.15,3.52-1.15,1.35,0,2.49.35,3.42,1.04.68.51,1.22,1.23,1.7,2.04l-2.83,16.09c-.59,1-1.15,1.91-1.7,2.72Z" />
        <polygon points="205.46 5.67 199.07 1.45 194.01 10.95 198.32 13.79 205.46 5.67" />
      </>
    ),
  },
};

export const Logo: React.FC<LogoProps> = ({
  type = 'lettertype',
  mode = 'light',
  className,
  ariaLabel = 'EvalTick',
}) => {
  const { viewBox, art } = MARKS[type];

  return (
    <svg
      className={cn(styles.logo, styles[mode], styles[type], className)}
      viewBox={viewBox}
      fill="currentColor"
      role="img"
      aria-label={ariaLabel}
    >
      {art}
    </svg>
  );
};

export default Logo;
